import { useQuery } from "@tanstack/react-query";
import { functionRepository } from "../../infra/repositories/function-repository";
import { AxiosError } from "axios";
import { Logger } from "../../infra/logger/logger";
import { BaseInputFilter } from "domain/models/base-input-filter";
import { useAuthStore } from "presentation/stores/use-auth-store";

const logger = Logger.getInstance();

/**
 * Hook customizado para buscar uma lista de "Funções" do sistema.
 * As funções são usadas para controlar permissões de acesso a diferentes partes da aplicação.
 * Utiliza o `useQuery` do `react-query` para gerir o estado do servidor, cache e refetching.
 *
 * @param {BaseInputFilter} filter - O objeto de filtro para a query (ex: paginação, ordenação).
 * @returns O resultado da query do `react-query`, contendo `data`, `isLoading`, `isError`, etc.
 */
export const useFunctions = (filter: BaseInputFilter) => {
  const { accessToken } = useAuthStore();

  return useQuery({
    queryKey: ["functions", filter],
    queryFn: async () => {
      try {
        const result = await functionRepository.getAll(filter);
        return result;
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data?.metadata?.errors?.[0],
            error.response?.data?.metadata
          );
          throw new Error(
            error.response?.data?.metadata?.errors?.[0] ||
              "An unexpected error occurred while fetching functions."
          );
        }
        logger.error("Unknown error while fetching functions", { error });
        throw error;
      }
    },
    enabled: !!accessToken,
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_DEFAULT) || 0,
  });
};
